"use client";

import React, { useRef } from "react";

import { Icon } from "@ospweb/sdk-ui";
import { useParentWidthStyle } from "../../hooks/useParentWidth";
import { useIsPc } from "@/src/hooks";

export interface FloatingPopupProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 关闭弹窗的回调函数 */
  onClose: () => void;
  /** 弹窗内容 */
  children: React.ReactNode;
  /** 自定义样式类名 */
  className?: string;
  /** 自定义位置 */
  position?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  /** 自定义尺寸 */
  size?: {
    width?: string;
    height?: string;
  };
  /** z-index 层级 */
  zIndex?: number;
  /** 宽度配置模式 */
  widthMode?: "right-fixed" | "centered";
  /** 父级容器引用，用于计算动态宽度 */
  parentRef?: React.RefObject<HTMLElement | HTMLDivElement | null>;

  padding?: number;
}

/**
 * FloatingPopup 组件
 *
 * 自定义悬浮弹窗组件，不干扰页面其他操作
 * 支持 PC 和移动端响应式定位，移动端通过 CSS 隐藏
 * 支持两种宽度配置模式：右固定和居中
 *
 * @example
 * ```tsx
 * <FloatingPopup
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="提示"
 *   widthMode="right-fixed"
 * >
 *   <div>弹窗内容</div>
 * </FloatingPopup>
 * ```
 */
export const FloatingPopup: React.FC<FloatingPopupProps> = ({
  open,
  onClose,
  children,
  className = "",
  position,
  size,
  zIndex = 999,
  widthMode = "right-fixed",
  parentRef,
  padding = 20,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isPc] = useIsPc();
  // 获取基于父级容器的动态宽度
  const parentWidthStyle = useParentWidthStyle(parentRef || null, {
    padding: padding,
    containerPadding: 16 * 2 - 24,
  });

  if (!open) {
    return null;
  }

  // 根据宽度模式配置位置和尺寸
  const getPositionAndSize = () => {
    if (widthMode === "centered") {
      return parentWidthStyle.width
        ? {
            position: {
              bottom: `${padding}px`,
              right: `${padding}px`,
              position: "fixed" as const,
            },
            size: {
              width: parentRef ? parentWidthStyle.width : "520px",
              minWidth: "320px",
            },
          }
        : {};
    } else {
      return {
        position: {
          bottom: `${padding}px`,
          right: `${padding}px`,
          position: "fixed" as const,
        },
        size: isPc
          ? {
              minWidth: "320px",
              width: "auto",
            }
          : {
              width: "calc(100vw - 16px)",
            },
      };
    }
  };

  const { position: defaultPosition, size: responsiveSize } =
    getPositionAndSize();

  const finalPosition = { ...defaultPosition, ...position };
  const finalSize = { ...responsiveSize, ...size };

  // 构建样式
  const containerStyle: React.CSSProperties = {
    zIndex,
    ...finalPosition,
    ...finalSize,
  };

  // 构建CSS类名
  const getCSSClasses = () => {
    const baseClasses = [
      "hidden xl:block",
      "bg-background_solid_primary",
      "shadow-2xl",
      "relative",
      // 'transition-all duration-300 ease-in-out',
      "overflow-hidden",
      "border border-border_white_transparent10",
      "rounded-[12px]",
      "p-6",
    ];

    return [...baseClasses, className].filter(Boolean).join(" ");
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      className={getCSSClasses()}
      style={containerStyle}
    >
      {/* 关闭按钮 */}
      <button
        data-openreplay-label="popup guide Button"
        id="btn-popup-guide-floatingpopup-floatingpopup"
        className="absolute right-2 top-2 z-10"
        onClick={onClose}
        aria-label="popup guide"
      >
        <div className="rounded-full size-6 flex items-center justify-center transition-colors cursor-pointer">
          <Icon name="close" size={24} className="text-content_primary" />
        </div>
      </button>
      {/* 背景装饰 - 右下角椭圆 */}
      <div className="absolute -bottom-[180.9px] flex h-[378.245px] items-center justify-center -right-[128.1px] w-[640.094px] pointer-events-none">
        <div className="flex-none rotate-[351.619deg]">
          <div className="h-[290px] relative w-[600px]">
            <div className="absolute -inset-[34.08%_-16.56%] bg-gradient-to-br from-[#BAE5FF]/15 via-[#BAE5FF]/5 to-[#BAE5FF]/0 rounded-full blur-2xl" />
          </div>
        </div>
      </div>

      {/* 背景装饰 - 左上角椭圆 */}
      <div className="absolute flex h-[411.756px] items-center justify-center -left-[65.45px] -top-[176.07px] w-[505.144px] pointer-events-none">
        <div className="flex-none rotate-[158.251deg]">
          <div className="h-[269.2px] relative w-[436.475px]">
            <div className="absolute -inset-[37.15%_-22.91%] bg-gradient-to-br from-[#BAE5FF]/15 via-[#BAE5FF]/5 to-[#BAE5FF]/0 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
      {/* 内容区域 */}
      {children}
    </div>
  );
};
